import { PERMS, PRESETS, parsePerms } from '../../shared/perms';
import { useMemo, useState } from 'react';
import {
  api, useApi, useLocation, setQuery, dollars, hours, fmtDate, fmtTime, fmtClock, toCents, centsInput, today, addDays,
  toLocalInput, fromLocalInput, downloadCsv, PAY_TYPES, type Me, type Row, openedForNew } from '../lib';
import { Badge, Button, Panel, Table, Loading, ErrorBox, Modal, Form, Field, MoneyField, Select, Tabs, Stat, Icon, Empty, useToast, confirmAction } from '../ui';
import { StaffFilePage, RolesTab, OutsideTechs, TimeOffTab, ExpiringBar, RolePick } from './StaffFile';

const payLabel = (t: string) => PAY_TYPES.find(p => p.value === t)?.label || t;
const rateLabel = (t: string, r: number) =>
  t === 'percent_labor' ? `${(r / 100).toFixed(1)}% of labor` : t === 'none' ? '—' : `${dollars(r)} ${({ hourly: '/ hr', salary_weekly: '/ week', salary_monthly: '/ month', per_job: '/ job' } as Row)[t] || ''}`;

export function Staff({ me }: { me: Me }) {
  const { query } = useLocation();
  const owner = me.user.role === 'owner';
  const tab = query.get('tab') || (owner ? 'team' : 'time');
  const tabs = [...(owner ? [{ id: 'team', label: 'Team' }, { id: 'roles', label: 'Roles' }, { id: 'outside', label: 'Outside techs' }, { id: 'timeoff', label: 'Time off' }] : []),
    { id: 'attendance', label: 'Attendance' }, { id: 'time', label: 'Time clock' }, ...(owner ? [{ id: 'payroll', label: 'Payroll' }] : [])];
  const person = query.get('person');
  return (
    <>
      <div className="page-head"><div><h1>Staff & Payroll</h1><p>Logins, time clock and pay — hourly, salary, or per job.</p></div></div>
      <Tabs value={tab} onChange={t => setQuery({ tab: t, run: '', person: '' })} tabs={tabs} />
      {tab === 'team' && owner && (person ? <StaffFilePage id={person} onBack={() => setQuery({ person: '' })} /> : <Team me={me} />)}
      {tab === 'roles' && owner && <RolesTab />}
      {tab === 'outside' && owner && <OutsideTechs />}
      {tab === 'timeoff' && owner && <TimeOffTab />}
      {tab === 'attendance' && <Attendance owner={owner} />}
      {tab === 'time' && <TimeSheets owner={owner} />}
      {tab === 'payroll' && owner && <Payroll />}
    </>
  );
}

// ───────── Team ─────────
function Team({ me }: { me: Me }) {
  const { data, error, reload } = useApi<Row[]>('/api/users');
  const [modal, setModal] = useState<null | 'add' | { edit: Row } | { pw: Row } | { temp: { name: string; login: string; password: string } }>(() => (openedForNew() ? 'add' : null));
  const toast = useToast();
  return (
    <>
      <div className="toolbar"><span className="spacer" /><Button kind="primary" icon="plus" onClick={() => setModal('add')}>Add staff member</Button></div>
      <ExpiringBar />
      <ErrorBox error={error} onRetry={reload} />
      {!data ? <Loading /> : (
        <Panel>
          <Table heads={['Name', 'Username', 'Role', 'Pay', 'Rate', 'Password', 'Status', '']} onRow={i => setQuery({ person: data[i].id })}
            rows={data.map(u => [<b className="linkish">{u.name}</b>, u.login, u.role === 'owner' ? <Badge tone="violet">Owner</Badge> : <RolePick u={u} onDone={reload} />,
              payLabel(u.pay_type), rateLabel(u.pay_type, u.pay_rate),
              u.must_change_password ? <Badge tone="amber">Temporary</Badge> : u.password_changed_at ? <small className="muted">changed {fmtDate(u.password_changed_at.slice(0, 10))}</small> : <span className="muted">—</span>,
              u.active ? <Badge tone="green">Active</Badge> : <Badge tone="gray">Inactive</Badge>,
              <span className="row-actions" onClick={e => e.stopPropagation()}>
                <Button kind="ghost" icon="clipboard" onClick={() => setQuery({ person: u.id })}>File</Button>
                <Button kind="ghost" onClick={() => setModal({ pw: u })}>Reset password</Button>
                <button className="icon-btn" title="Edit" onClick={() => setModal({ edit: u })}><Icon name="edit" size={16} /></button>
              </span>])} />
          <p className="form-note pad">Mechanics sign in on their phone with their username and password. They see every job but never prices, invoices or anyone's pay. Everyone picks their own password the first time they sign in — nobody else can see it. Forgot it? Reset password gives a temporary one. Tap a name for their file: contact, SSN, CDL, documents, advances, tools, time off.</p>
        </Panel>
      )}
      {(modal === 'add' || (typeof modal === 'object' && modal && 'edit' in modal)) && (
        <StaffModal u={typeof modal === 'object' ? (modal as { edit: Row }).edit : undefined} self={me.user.id}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); reload(); }} />
      )}
      {typeof modal === 'object' && modal && 'pw' in modal && (
        <Modal title={`Reset password for ${modal.pw.name}?`} onClose={() => setModal(null)}>
          <Form onCancel={() => setModal(null)} submitLabel="Make a temporary password" onSubmit={async () => {
            const r = await api<{ login: string; password: string }>(`/api/users/${modal.pw.id}/password`, { body: {} });
            setModal({ temp: { name: modal.pw.name, login: r.login, password: r.password } }); reload();
          }}>
            <p className="full">They are signed out on all devices and get a temporary password. When they sign in with it, they choose their own — you won't see that one.</p>
          </Form>
        </Modal>
      )}
      {typeof modal === 'object' && modal && 'temp' in modal && (
        <Modal title={`Temporary password for ${modal.temp.name}`} onClose={() => setModal(null)}>
          <div className="modal-pad">
          <p>Username: <b>{modal.temp.login}</b></p>
          <div className="temp-pass">{modal.temp.password}</div>
          <p className="form-note">Tell them in person or by phone. It's shown only now.</p>
          <div className="actions"><Button kind="primary" onClick={() => { void navigator.clipboard?.writeText(modal.temp.password).then(() => toast('Copied')); }}>Copy</Button><Button onClick={() => setModal(null)}>Done</Button></div></div>
        </Modal>
      )}
    </>
  );
}

function StaffModal({ u, self, onClose, onSaved }: { u?: Row; self: string; onClose: () => void; onSaved: () => void }) {
  const [payType, setPayType] = useState(u?.pay_type || 'hourly');
  const [role, setRole] = useState<string>(u?.role || 'mechanic');
  const [perms, setPerms] = useState<Record<string, boolean>>(() => parsePerms(u?.perms));
  const roles = useApi<Row[]>('/api/roles');
  const [roleId, setRoleId] = useState<string>(() => u ? (u.role_id || '') : 'role-mechanic');
  const unit = PAY_TYPES.find(p => p.value === payType)?.unit || '';
  return (
    <Modal title={u ? `Edit ${u.name}` : 'Add staff member'} onClose={onClose}>
      <Form onCancel={onClose} onSubmit={async v => {
        const pay_rate = payType === 'percent_labor' ? Math.round(Number(v.pay_rate || 0) * 100) : toCents(v.pay_rate);
        const body = { ...v, pay_type: payType, pay_rate, weekly_hours: Math.round(Number(v.weekly_hours || 0) * 100), absent_rate: toCents(v.absent_rate), active: u ? v.active === 'on' : true, temporary: !u, ...(role === 'manager' && !roleId ? { perms } : {}), role_id: role === 'owner' ? null : roleId || null,
          disc_pct: role === 'manager' ? Math.round(Number(v.disc_pct || 0) * 100) : 0, disc_amt: role === 'manager' ? toCents(v.disc_amt || '0') : 0 };
        if (u) await api(`/api/users/${u.id}`, { method: 'PATCH', body }); else await api('/api/users', { body });
        onSaved();
      }}>
        <Field label="Full name" name="name" required defaultValue={u?.name} />
        <Field label="Username (for sign in)" name="login" required defaultValue={u?.login} autoCapitalize="none" />
        <Field label="Phone" name="phone" type="tel" defaultValue={u?.phone} />
        <Select label="Role" name="role_pick" value={u?.id === self ? 'owner' : roleId || (role === 'manager' ? 'custom' : role)} disabled={u?.id === self}
          onChange={e => { const val = e.target.value; if (val === 'owner') { setRole('owner'); setRoleId(''); } else if (val === 'custom') { setRole('manager'); setRoleId(''); } else { const r = (roles.data || []).find(x => x.id === val); setRoleId(val); setRole(r?.base || 'mechanic'); } }}
          hint="Roles are set up in the Roles tab. Owner: everything.">
          {(roles.data || []).map(r => <option key={r.id} value={r.id}>{r.name}{r.base === 'mechanic' ? ' (phone app)' : ''}</option>)}
          <option value="custom">Manager — pick switches myself</option><option value="owner">Owner</option>
        </Select>
        <input type="hidden" name="role" value={u?.id === self ? 'owner' : role} />
        {role === 'manager' && (
          <div className="perm-box full">
            {!roleId && <><b>What this manager can see and do</b>
            <div className="perm-presets">{Object.entries(PRESETS).map(([k, p]) => <button key={k} type="button" className="chip" onClick={() => setPerms({ ...p.perms })}>{p.label}</button>)}</div>
            {PERMS.map(p => <label key={p.key} className="check"><input type="checkbox" checked={!!perms[p.key]} onChange={e => setPerms(x => ({ ...x, [p.key]: e.target.checked }))} /> {p.label}</label>)}
            <small className="muted">Always owner-only: payroll, staff and passwords, settings, integrations. Every price change is in the activity log.</small></>}
            <b className="perm-sub">Discounts</b>
            <div className="form-body two">
              <Field label="Up to % off" name="disc_pct" type="number" step="0.5" min={0} max={100} defaultValue={u?.disc_pct ? String(u.disc_pct / 100) : ''} placeholder="0 = none" />
              <MoneyField label="Up to $ per work order" name="disc_amt" defaultValue={u?.disc_amt ? centsInput(u.disc_amt) : ''} placeholder="0 = no $ cap" />
            </div>
            <small className="muted">Both empty = can't give discounts. Over the limit, the discount is sent to you to approve; the job can't be invoiced until you do.</small>
          </div>
        )}
        <Select label="How they're paid" name="pay_type_display" value={payType} onChange={e => setPayType(e.target.value)}>
          {PAY_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </Select>
        {payType !== 'none' && (payType === 'percent_labor'
          ? <Field label="Percent of labor" name="pay_rate" type="number" step="0.1" min={0} max={100} defaultValue={u ? u.pay_rate / 100 : ''} required />
          : <MoneyField label={`Rate (${unit})`} name="pay_rate" defaultValue={centsInput(u?.pay_rate)} required />)}
        {payType === 'hourly' && <Field label="Usual hours per week" name="weekly_hours" type="number" step="0.5" min={0} max={168} defaultValue={u?.weekly_hours ? u.weekly_hours / 100 : ''} hint="For part-timers, e.g. 20. Blank = full shifts. Used for the monthly cost estimate." />}
        {payType.startsWith('salary') && <MoneyField label="Deduct per missed day" name="absent_rate" defaultValue={centsInput(u?.absent_rate)} hint="Blank = salary ÷ work days" />}
        {!u && <Field label="Password" name="password" type="text" minLength={6} required autoComplete="off" hint="Temporary: they choose their own the first time they sign in." />}
        {u && u.id !== self && <label className="check full"><input type="checkbox" name="active" defaultChecked={!!u.active} /> Active (uncheck when someone leaves — signs them out)</label>}
        {u && u.id === self && <input type="hidden" name="active" value="on" />}
      </Form>
    </Modal>
  );
}

// ───────── Time sheets ─────────
function mondayOf(d: string) { const dt = new Date(d + 'T12:00:00Z'); return addDays(d, -((dt.getUTCDay() + 6) % 7)); }

function TimeSheets({ owner }: { owner: boolean }) {
  const [from, setFrom] = useState(mondayOf(today()));
  const [to, setTo] = useState(addDays(mondayOf(today()), 6));
  const [user, setUser] = useState('');
  const users = useApi<Row[]>('/api/users');
  const { data, error, reload } = useApi<Row[]>(`/api/time?from=${from}&to=${to}&user_id=${user}`, [from, to, user]);
  const [modal, setModal] = useState<null | 'add' | { edit: Row }>(null);
  const toast = useToast();
  const localDay = (iso: string) => toLocalInput(iso).slice(0, 10);
  const rows = (data || []).filter(t => localDay(t.clock_in) >= from && localDay(t.clock_in) <= to);
  const dur = (t: Row) => Math.round(((t.clock_out ? Date.parse(t.clock_out) : Date.now()) - Date.parse(t.clock_in)) / 36000);
  const totals = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of rows) if (t.clock_out) m[t.name] = (m[t.name] || 0) + dur(t);
    return Object.entries(m).sort();
  }, [rows]);

  async function del(id: string) {
    if (!confirmAction('Delete this time entry?')) return;
    try { await api(`/api/time/${id}`, { method: 'DELETE' }); reload(); } catch (e) { toast((e as Error).message, 'err'); }
  }

  return (
    <>
      <div className="toolbar">
        <Button onClick={() => { setFrom(addDays(from, -7)); setTo(addDays(to, -7)); }} aria-label="Previous week"><Icon name="back" size={16} /></Button>
        <label className="inline">From <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
        <label className="inline">To <input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
        <Button onClick={() => { setFrom(addDays(from, 7)); setTo(addDays(to, 7)); }} aria-label="Next week"><Icon name="back" size={16} className="flip" /></Button>
        <select value={user} onChange={e => setUser(e.target.value)} aria-label="Staff"><option value="">Everyone</option>{users.data?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
        <span className="spacer" />
        <Button icon="download" onClick={() => downloadCsv(`time-${from}-to-${to}.csv`, ['Name', 'In', 'Out', 'Hours', 'Job', 'Note'],
          rows.map(t => [t.name, t.clock_in, t.clock_out, t.clock_out ? (dur(t) / 100).toFixed(2) : 'open', t.wo_number, t.note]))}>CSV</Button>
        {owner && <Button kind="primary" icon="plus" onClick={() => setModal('add')}>Add entry</Button>}
      </div>
      <div className="stats">{totals.length ? totals.map(([n, h]) => <Stat key={n} label={n} value={`${hours(h)} h`} hint={h > 4000 ? `${hours(h - 4000)} h over 40` : undefined} />) : <Stat label="Total" value="0.00 h" />}</div>
      <ErrorBox error={error} onRetry={reload} />
      {!data ? <Loading /> : (
        <Panel>
          <Table heads={['Name', 'Day', 'In', 'Out', 'Hours', 'Job', 'Note', '']} empty="No time recorded in this period."
            rows={rows.map(t => [<b>{t.name}</b>, fmtDate(localDay(t.clock_in)), fmtClock(t.clock_in), t.clock_out ? fmtClock(t.clock_out) : <Badge tone="green">On the clock</Badge>,
              hours(dur(t)), t.wo_number || '—', <small>{t.note}{t.edited_by_name ? <span className="muted"> (edited by {t.edited_by_name})</span> : ''}</small>,
              owner && <span className="row-actions">
                <button className="icon-btn" title="Edit" onClick={() => setModal({ edit: t })}><Icon name="edit" size={16} /></button>
                <button className="icon-btn" title="Delete" onClick={() => del(t.id)}><Icon name="trash" size={16} /></button></span>])} />
        </Panel>
      )}
      {modal && (
        <Modal title={modal === 'add' ? 'Add time entry' : 'Edit time entry'} onClose={() => setModal(null)}>
          <Form onCancel={() => setModal(null)} onSubmit={async v => {
            const body = { user_id: v.user_id, clock_in: fromLocalInput(v.clock_in), clock_out: v.clock_out ? fromLocalInput(v.clock_out) : null, note: v.note };
            if (modal === 'add') await api('/api/time', { body }); else await api(`/api/time/${modal.edit.id}`, { method: 'PATCH', body: { ...body, work_order_id: modal.edit.work_order_id } });
            setModal(null); reload();
          }}>
            <Select label="Staff member" name="user_id" key={users.data ? "l" : "n"} defaultValue={modal === 'add' ? user : modal.edit.user_id} required full disabled={modal !== 'add'}>
              <option value="">Select…</option>{users.data?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </Select>
            {modal !== 'add' && <input type="hidden" name="user_id" value={modal.edit.user_id} />}
            <Field label="Clock in" name="clock_in" type="datetime-local" required defaultValue={modal === 'add' ? `${today()}T07:00` : toLocalInput(modal.edit.clock_in)} />
            <Field label="Clock out" name="clock_out" type="datetime-local" defaultValue={modal === 'add' ? `${today()}T16:00` : toLocalInput(modal.edit.clock_out)} />
            <Field label="Reason / note" name="note" full defaultValue={modal === 'add' ? 'Forgot to clock in' : modal.edit.note} />
          </Form>
        </Modal>
      )}
    </>
  );
}

// ───────── Payroll ─────────
function Payroll() {
  const { query } = useLocation();
  const runId = query.get('run');
  const { data, error, reload } = useApi<Row[]>('/api/payroll/runs');
  const [creating, setCreating] = useState(false);
  if (runId) return <PayrollRun id={runId} onBack={() => { setQuery({ run: '' }); reload(); }} />;
  const lastEnd = data?.[0]?.period_end;
  const nextStart = lastEnd ? addDays(lastEnd, 1) : mondayOf(addDays(today(), -7));
  return (
    <>
      <div className="toolbar"><span className="muted">Hours come from the time clock. Salaries are prorated by days in the period. Use Monday–Sunday periods so overtime is right.</span>
        <span className="spacer" /><Button kind="primary" icon="plus" onClick={() => setCreating(true)}>Run payroll</Button></div>
      <ErrorBox error={error} onRetry={reload} />
      {!data ? <Loading /> : (
        <Panel>
          <Table heads={['Period', 'People', 'Total', 'Status', 'Paid on']} empty="No payroll yet. Click “Run payroll” to start."
            rows={data.map(r => [<b>{fmtDate(r.period_start)} – {fmtDate(r.period_end)}</b>, r.people, dollars(r.total), <Badge>{r.status}</Badge>, fmtDate(r.paid_on)])}
            onRow={i => setQuery({ run: data[i].id })} />
        </Panel>
      )}
      {creating && (
        <Modal title="Run payroll" onClose={() => setCreating(false)}>
          <Form submitLabel="Calculate" onCancel={() => setCreating(false)} onSubmit={async v => {
            const r = await api<{ id: string }>('/api/payroll/runs', { body: v }); setCreating(false); setQuery({ run: r.id });
          }}>
            <Field label="Period start" name="period_start" type="date" required defaultValue={nextStart} />
            <Field label="Period end" name="period_end" type="date" required defaultValue={addDays(nextStart, 6)} />
            <p className="form-note full">Weekly = 7 days, every two weeks = 14 days. You can add bonuses or deductions before marking it paid.</p>
          </Form>
        </Modal>
      )}
    </>
  );
}

function PayrollRun({ id, onBack }: { id: string; onBack: () => void }) {
  const { data, error, reload } = useApi<Row>(`/api/payroll/runs/${id}`);
  const [edit, setEdit] = useState<Row | null>(null);
  const [paying, setPaying] = useState(false);
  const toast = useToast();
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const run = data.run as Row;
  const items = data.items as Row[];
  const draft = run.status === 'Draft';
  const total = items.reduce((a, i) => a + i.total, 0);
  return (
    <>
      <div className="toolbar">
        <Button onClick={onBack}><Icon name="back" size={16} />All payroll</Button>
        <h3 className="toolbar-title">{fmtDate(run.period_start)} – {fmtDate(run.period_end)} <Badge>{run.status}</Badge></h3>
        <span className="spacer" />
        <Button icon="printer" onClick={() => window.print()}>Print</Button>
        <Button icon="download" onClick={() => downloadCsv(`payroll-${run.period_start}.csv`, ['Name', 'Pay type', 'Rate', 'Regular h', 'OT h', 'Base', 'Adjustment', 'Total', 'Note', 'Reference'],
          items.map(i => [i.name, payLabel(i.pay_type), i.pay_type === 'percent_labor' ? i.pay_rate / 100 + '%' : i.pay_rate / 100, hours(i.regular_hours), hours(i.overtime_hours), i.base_pay / 100, (i.adjustment + i.bonus - i.advance_deduct) / 100, i.total / 100, i.note, i.reference]))}>CSV</Button>
        {draft && <Button icon="refresh" onClick={async () => { await api(`/api/payroll/runs/${id}/recalculate`, { body: {} }); toast('Recalculated from time clock'); reload(); }}>Recalculate</Button>}
        {draft && <Button kind="danger" onClick={async () => { if (confirmAction('Delete this draft payroll?')) { await api(`/api/payroll/runs/${id}`, { method: 'DELETE' }); onBack(); } }}>Delete</Button>}
        {draft && <Button kind="primary" icon="check" onClick={() => setPaying(true)}>Mark as paid</Button>}
      </div>
      {(data.warnings as string[]).map(w => <div key={w} className="note-bar warn"><Icon name="alert" size={16} />{w}</div>)}
      <div className="stats"><Stat label="Total payroll" value={dollars(total)} /><Stat label="People" value={items.length} />
        <Stat label="Overtime hours" value={hours(items.reduce((a, i) => a + i.overtime_hours, 0))} /></div>
      <Panel>
        <Table heads={['Name', 'Pay', 'Regular h', 'OT h', 'Base pay', 'Attendance', 'Bonus (rule)', 'Advance back', 'Bonus / deduction', 'Total', 'Note / ref', '']}
          rows={items.map(i => [<b>{i.name}</b>, <small>{rateLabel(i.pay_type, i.pay_rate)}</small>, i.pay_type === 'hourly' ? hours(i.regular_hours) : '—',
            i.overtime_hours ? <b>{hours(i.overtime_hours)}</b> : '—', dollars(i.base_pay),
            i.attendance_adj || i.attendance_note ? <span><span className={i.attendance_adj < 0 ? 'danger' : ''}>{i.attendance_adj ? dollars(i.attendance_adj) : '$0.00'}</span><small className="muted">{i.attendance_note}</small></span> : '—',
            i.bonus ? <span title={i.bonus_note}>{dollars(i.bonus)}<small className="muted">{i.bonus_note}</small></span> : '—',
            i.advance_deduct ? <span className="danger">−{dollars(i.advance_deduct)}</span> : '—',
            i.adjustment ? <span className={i.adjustment < 0 ? 'danger' : ''}>{dollars(i.adjustment)}</span> : '—', <b>{dollars(i.total)}</b>,
            <small>{i.note}{i.reference ? ` · ${i.reference}` : ''}</small>,
            draft && <button className="icon-btn" title="Adjust" onClick={() => setEdit(i)}><Icon name="edit" size={16} /></button>])} />
        <p className="form-note pad">Gross pay only. Payroll taxes and withholding are not calculated — give these totals to your accountant or payroll service.</p>
      </Panel>
      {edit && (
        <Modal title={`Adjust — ${edit.name}`} onClose={() => setEdit(null)}>
          <Form onCancel={() => setEdit(null)} onSubmit={async v => {
            await api(`/api/payroll/items/${edit.id}`, { method: 'PATCH', body: { adjustment: (v.sign === '-' ? -1 : 1) * toCents(v.amount), note: v.note, reference: v.reference } });
            setEdit(null); reload();
          }}>
            <Select label="Type" name="sign" defaultValue={edit.adjustment < 0 ? '-' : '+'}><option value="+">Bonus / extra (+)</option><option value="-">Deduction / advance (−)</option></Select>
            <MoneyField label="Amount" name="amount" defaultValue={centsInput(Math.abs(edit.adjustment))} />
            <Field label="Note" name="note" full defaultValue={edit.note} placeholder="e.g. Saturday call-out, tool advance" />
            <Field label="Check # / transfer ref" name="reference" full defaultValue={edit.reference} />
          </Form>
        </Modal>
      )}
      {paying && (
        <Modal title="Mark payroll as paid" onClose={() => setPaying(false)}>
          <Form submitLabel="Mark paid & lock" onCancel={() => setPaying(false)} onSubmit={async v => {
            await api(`/api/payroll/runs/${id}/paid`, { body: v }); setPaying(false); toast('Payroll locked as paid'); reload();
          }}>
            <Field label="Date paid" name="paid_on" type="date" defaultValue={today()} required />
            <p className="form-note full">This records that you paid {dollars(total)}. It does not send money. Paid payroll can't be edited.</p>
          </Form>
        </Modal>
      )}
    </>
  );
}



// ───────── Attendance ─────────
const STATUS: Record<string, { label: string; short: string; tone: string }> = {
  present: { label: 'Present', short: 'Present', tone: 'green' },
  late: { label: 'Late', short: 'Late', tone: 'amber' },
  absent: { label: 'Absent (no-show)', short: 'Absent', tone: 'red' },
  excused: { label: 'Excused (sick / approved)', short: 'Excused', tone: 'blue' },
  off: { label: 'Day off', short: 'Off', tone: 'gray' },
};
const minutesOf = (iso: string) => { const t = toLocalInput(iso).slice(11); const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const hhmm = (s: string) => { const [h, m] = s.split(':').map(Number); return h * 60 + m; };

function Attendance({ owner }: { owner: boolean }) {
  const [week, setWeek] = useState(mondayOf(today()));
  const { data, error, reload } = useApi<Row>(`/api/attendance?week=${week}`, [week]);
  const [cell, setCell] = useState<{ u: Row; day: string } | null>(null);
  const toast = useToast();
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const sched = data.schedule as Row;
  const mark = (uid: string, day: string) => (data.marks as Row[]).find(m => m.user_id === uid && m.day === day);
  const clock = (uid: string, day: string) => (data.clock as Row)[uid + '|' + day];
  const workDay = (i: number) => i < sched.work_days;
  const t = today();
  async function allPresent(day: string) {
    const todo = (data!.staff as Row[]).filter(u => !mark(u.id, day));
    for (const u of todo) {
      const c = clock(u.id, day);
      const late = c ? Math.max(0, minutesOf(c.first_in) - hhmm(sched.shift_start)) : 0;
      await api('/api/attendance', { method: 'PUT', body: late > 5 ? { user_id: u.id, day, status: 'late', late_minutes: late } : { user_id: u.id, day, status: 'present' } });
    }
    toast(todo.length ? `Marked ${todo.length} people for ${fmtDate(day)}` : 'Everyone already has a mark'); reload();
  }
  return (
    <>
      <div className="toolbar">
        <Button onClick={() => setWeek(addDays(week, -7))} aria-label="Previous week"><Icon name="back" size={16} /></Button>
        <b>{fmtDate(week)} – {fmtDate(addDays(week, 6))}</b>
        <Button onClick={() => setWeek(addDays(week, 7))} disabled={week >= mondayOf(t)} aria-label="Next week"><Icon name="back" size={16} className="flip" /></Button>
        {week !== mondayOf(t) && <Button kind="ghost" onClick={() => setWeek(mondayOf(t))}>This week</Button>}
        <span className="spacer" />
        {(data.days as string[]).includes(t) && <Button onClick={async () => {
          if (!confirmAction('No more work today? Everyone on the clock is clocked out now and marked "left early — with permission". Nobody loses pay.')) return;
          try { const r = await api<Row>('/api/attendance/send-home', { body: {} }); toast(r.sent ? `Sent home: ${(r.names as string[]).join(', ')}${r.early ? ` (${r.early} min early, no pay taken off)` : ''}` : 'Nobody is on the clock'); reload(); } catch (e) { toast((e as Error).message, 'err'); }
        }}>Send everyone home</Button>}
        {(data.days as string[]).includes(t) && <Button kind="primary" icon="check" onClick={() => allPresent(t)}>Mark today for everyone</Button>}
      </div>
      <p className="form-note">Shift {sched.shift_start}–{sched.shift_end}, {['', '', '', '', '', 'Mon–Fri', 'Mon–Sat', 'every day'][sched.work_days]}. Tap a day to mark it.
        {owner && ' Late minutes are deducted and extra hours are paid on the next payroll for salaried staff. Hourly staff are paid from the time clock.'}</p>
      <Panel>
        <div className="table-wrap att-wrap">
          <table className="att">
            <thead><tr><th>Name</th>{(data.days as string[]).map((d, i) => <th key={d} className={(workDay(i) ? '' : 'off ') + (d === t ? 'today' : '')}>{new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })}<small>{Number(d.slice(8))}</small></th>)}<th>Week</th></tr></thead>
            <tbody>
              {(data.staff as Row[]).map(u => (
                <tr key={u.id}>
                  <td><b>{u.name}</b><small className="muted">{u.pay_type === 'hourly' ? 'Hourly' : u.pay_type.startsWith('salary') ? 'Salary' : u.role}</small></td>
                  {(data.days as string[]).map((d, i) => {
                    const m = mark(u.id, d); const c = clock(u.id, d);
                    const lateGuess = c && !m && minutesOf(c.first_in) - hhmm(sched.shift_start) > 5;
                    return (
                      <td key={d} className={'att-cell ' + (workDay(i) ? '' : 'off ') + (d === t ? 'today' : '')}>
                        <button onClick={() => setCell({ u, day: d })} disabled={d > t} aria-label={`${u.name}, ${fmtDate(d)}: ${m ? STATUS[m.status].label : 'not marked'}`}>
                          {m ? <Badge tone={STATUS[m.status].tone}>{STATUS[m.status].short}{m.status === 'late' ? ` ${m.late_minutes}m` : ''}</Badge>
                            : <span className="att-empty">{workDay(i) && d <= t ? '+ Mark' : ''}</span>}
                          {m && m.extra_hours > 0 && <span className="att-extra">+{hours(m.extra_hours)}h</span>}
                          {m?.half_day ? <span className="att-early own">½ day</span> : m?.early_minutes > 0 ? <span className={'att-early' + (m.early_ok ? '' : ' own')}>left {m.early_minutes}m early{m.early_ok ? ' ✓' : ''}</span> : null}
                          {c && <small className={lateGuess ? 'warn-text' : 'muted'}>{fmtClock(c.first_in)}–{c.last_out ? fmtClock(c.last_out) : 'now'}</small>}
                          {m?.note && <small className="muted clip1">{m.note}</small>}
                        </button>
                      </td>
                    );
                  })}
                  <td className="att-sum"><small>{u.summary || '—'}</small>{owner && u.adjustment ? <b className={u.adjustment < 0 ? 'danger' : ''}>{u.adjustment > 0 ? '+' : ''}{dollars(u.adjustment)}</b> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!(data.staff as Row[]).length && <Empty>Add staff in the Team tab first.</Empty>}
      </Panel>
      {cell && <MarkModal u={cell.u} day={cell.day} existing={mark(cell.u.id, cell.day)} clockRow={clock(cell.u.id, cell.day)} sched={sched}
        onClose={() => setCell(null)} onSaved={() => { setCell(null); reload(); }} />}
    </>
  );
}

function MarkModal({ u, day, existing, clockRow, sched, onClose, onSaved }: { u: Row; day: string; existing?: Row; clockRow?: Row; sched: Row; onClose: () => void; onSaved: () => void }) {
  const lateGuess = clockRow ? Math.max(0, minutesOf(clockRow.first_in) - hhmm(sched.shift_start)) : 0;
  const extraGuess = clockRow?.last_out ? Math.max(0, minutesOf(clockRow.last_out) - hhmm(sched.shift_end)) : 0;
  const [status, setStatus] = useState<string>(existing?.status || (lateGuess > 5 ? 'late' : 'present'));
  const [late, setLate] = useState(String(existing?.late_minutes || (lateGuess > 5 ? lateGuess : '')));
  const [extra, setExtra] = useState(existing?.extra_hours ? (existing.extra_hours / 100).toString() : extraGuess >= 15 ? (Math.round(extraGuess / 15) / 4).toString() : '');
  const [note, setNote] = useState(existing?.note || '');
  const earlyGuess = clockRow?.last_out ? Math.max(0, hhmm(sched.shift_end) - minutesOf(clockRow.last_out)) : 0;
  const [early, setEarly] = useState(String(existing?.early_minutes || (earlyGuess >= 10 ? earlyGuess : '')));
  const [earlyOk, setEarlyOk] = useState(existing ? existing.early_ok !== 0 : true);
  const [half, setHalf] = useState(!!existing?.half_day);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function save(body: Row) {
    setBusy(true); setErr('');
    try { await api('/api/attendance', { method: 'PUT', body: { user_id: u.id, day, ...body } }); onSaved(); } catch (e) { setErr((e as Error).message); setBusy(false); }
  }
  return (
    <Modal title={`${u.name} — ${new Date(day + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' })}`} onClose={onClose}>
      <div className="form">
        <div className="form-body">
          {clockRow && <p className="form-note full">Time clock: in {fmtClock(clockRow.first_in)}, out {clockRow.last_out ? fmtClock(clockRow.last_out) : '— still clocked in'} · {hours(clockRow.hours)} h. Shift is {sched.shift_start}–{sched.shift_end}.</p>}
          <div className="status-pick full" role="radiogroup" aria-label="Attendance">
            {Object.entries(STATUS).map(([k, s]) => (
              <button key={k} type="button" role="radio" aria-checked={status === k} className={'st-' + s.tone + (status === k ? ' on' : '')} onClick={() => setStatus(k)}>{s.label}</button>
            ))}
          </div>
          {status === 'late' && <label className="field"><span>Minutes late *</span><input type="number" min={1} max={720} value={late} onChange={e => setLate(e.target.value)} /></label>}
          {(status === 'present' || status === 'late') && <label className="field"><span>Extra hours (stayed late)</span><input type="number" step="0.25" min={0} max={24} value={extra} onChange={e => setExtra(e.target.value)} placeholder="0" /></label>}
          {(status === 'present' || status === 'late') && <>
            <label className="check full"><input type="checkbox" checked={half} onChange={e => setHalf(e.target.checked)} /> Half day (half a day's pay taken off for salaried staff)</label>
            {!half && <><label className="field"><span>Left early (minutes){earlyGuess >= 10 ? ` — clock says ${earlyGuess}` : ''}</span><input type="number" min={0} max={720} value={early} onChange={e => setEarly(e.target.value)} placeholder="0" /></label>
              {Number(early) > 0 && <div className="status-pick full" role="radiogroup" aria-label="Left early">
                <button type="button" role="radio" aria-checked={earlyOk} className={'st-green' + (earlyOk ? ' on' : '')} onClick={() => setEarlyOk(true)}>With my permission — no deduction</button>
                <button type="button" role="radio" aria-checked={!earlyOk} className={'st-amber' + (!earlyOk ? ' on' : '')} onClick={() => setEarlyOk(false)}>Left on their own{sched.early_deducts ? ' — minutes deducted' : ' (not deducted — Settings)'}</button>
              </div>}</>}
          </>}
          <label className="field full"><span>Note</span><input value={note} onChange={e => setNote(e.target.value)} placeholder={status === 'absent' ? 'e.g. no call, no show' : status === 'excused' ? 'e.g. doctor appointment' : ''} /></label>
        </div>
        {err && <div className="error-box"><Icon name="alert" />{err}</div>}
        <div className="form-foot">
          {existing && <Button kind="ghost" onClick={() => save({ status: 'clear' })} disabled={busy}>Remove mark</Button>}
          <span className="spacer" />
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button kind="primary" busy={busy} onClick={() => save({ status, late_minutes: Number(late) || 0, extra_hours: Math.round((Number(extra) || 0) * 100), note, half_day: half, early_minutes: half ? 0 : Number(early) || 0, early_ok: earlyOk })}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}
